// src/pages/Users/LastGallerie.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllGalleries } from "../../services/gallerieServices";
import Button from "../../components/Button/Button";
import { useUser } from "../Home/UserContext";

// ✅ rend les /uploads/... absolues vers l’API, laisse passer les https://
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";

const LastGallerie = () => {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const allGalleries = await getAllGalleries();
        const activeGalleries = (allGalleries || []).filter((g) => g.is_active);
        setGalleries(activeGalleries.slice(0, 3));
      } catch (error) {
        console.error("Failed to fetch galleries", error);
      }
    };
    fetchGalleries();
  }, []);

  const handleReadMore = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  const openGalleryModal = (gallery) => setSelectedGallery(gallery);
  const closeGalleryModal = () => {
    setSelectedGallery(null);
    setSelectedImage(null);
  };
  const openImageModal = (imageUrl) => setSelectedImage(imageUrl);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  // Helpers: tri des photos et sélection de la couverture
  const orderPhotos = React.useCallback((photos = []) => {
    const arr = Array.isArray(photos) ? [...photos] : [];
    arr.sort((a, b) => {
      const pa = Number.isFinite(a?.position)
        ? a.position
        : Number.MAX_SAFE_INTEGER;
      const pb = Number.isFinite(b?.position)
        ? b.position
        : Number.MAX_SAFE_INTEGER;
      return pa - pb || (a?.id ?? 0) - (b?.id ?? 0);
    });
    return arr;
  }, []);

  const getCoverPhoto = React.useCallback(
    (gallery) => {
      const ordered = orderPhotos(gallery?.photos || []);
      // 1) is_cover === true, 2) 1ère par position, 3) plus petit id
      return (
        ordered.find((p) => p?.is_cover === true) ||
        ordered[0] ||
        (Array.isArray(gallery?.photos)
          ? [...gallery.photos].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0))[0]
          : null)
      );
    },
    [orderPhotos]
  );

  return (
    <section className="py-12 bg-lightBackground dark:bg-darkBackground text-black dark:text-white">
      <div className="container mx-auto">
        <h2 className="text-3xl font-extrabold mb-4 text-primary mx-auto text-center font-satoshi uppercase">
          Derniers Albums Photos
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          {galleries.map((gallery) => {
            const coverPhoto = getCoverPhoto(gallery);
            const coverSrc = coverPhoto?.photo_url
              ? toAbsPhoto(coverPhoto.photo_url)
              : FALLBACK;

            return (
              <div
                key={gallery.id}
                className="flex flex-col bg-slate-100 dark:bg-slate-500 shadow-md rounded-lg p-4 justify-between h-full"
              >
                <div
                  className="cursor-pointer"
                  onClick={() => openGalleryModal(gallery)}
                >
                  <img
                    src={coverSrc || FALLBACK}
                    alt={gallery.titre}
                    className="w-full h-64 object-cover rounded-t-lg"
                    onError={onImgError}
                  />

                  <h3 className="text-xl font-bold mb-2 font-satoshi mt-4">
                    {gallery.titre}
                  </h3>

                  <div
                    className="text-sm text-gray-700 dark:text-gray-300 font-satoshi"
                    dangerouslySetInnerHTML={{ __html: gallery.description }}
                  />
                </div>

                {user && (
                  <Button
                    text="Voir Album"
                    onClick={() => handleReadMore(gallery.id)}
                    className="self-center mt-auto"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Modale Galerie */}
        {selectedGallery && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={closeGalleryModal}
          >
            <div
              className="relative bg-white dark:bg-gray-800 p-4 rounded-lg max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()} // évite la fermeture si on clique dans la modale
            >
              <button
                className="absolute top-2 right-2 text-black dark:text-white text-2xl"
                onClick={closeGalleryModal}
                aria-label="Fermer"
              >
                &times;
              </button>

              <h2 className="text-2xl font-bold text-center mb-4 dark:text-white">
                {selectedGallery.titre}
              </h2>

              <div className="grid grid-cols-3 gap-2">
                {orderPhotos(selectedGallery.photos || []).map((photo) => {
                  const thumb = toAbsPhoto(photo.photo_url);
                  return (
                    <img
                      key={photo.id}
                      src={thumb || FALLBACK}
                      alt={photo.titre || selectedGallery.titre}
                      className="w-full h-32 object-cover rounded-lg cursor-pointer"
                      onError={onImgError}
                      onClick={(e) => {
                        e.stopPropagation(); // ne ferme pas la modale galerie
                        openImageModal(photo.photo_url);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Modale Image plein écran */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <img
                src={toAbsPhoto(selectedImage) || FALLBACK}
                alt="Agrandissement"
                className="max-w-[90vw] max-h-[90vh] rounded-lg"
                onError={onImgError}
              />
              <button
                className="absolute top-2 right-2 text-white text-2xl"
                onClick={() => setSelectedImage(null)}
                aria-label="Fermer"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LastGallerie;
